import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Zap, Sun, Moon, Menu, X, Github, Youtube, Linkedin, MessageCircle } from "lucide-react";
import { usePublicTheme } from "../lib/theme";
import { useAuth } from "../context/AuthContext";
import { ChatWidget } from "./ChatWidget";

const NAV = [
  { to: "/",         label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/about",    label: "About" },
  { to: "/code",     label: "Code" },
  { to: "/contact",  label: "Contact" },
  { to: "/legal",    label: "Legal" },
];

export function PublicNav() {
  const { theme, toggle } = usePublicTheme();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > lastY.current && y > 64);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 w-full backdrop-blur-xl bg-[var(--bg)]/95 border-b border-[var(--bd)] transition-transform duration-300 ${hidden ? "-translate-y-full" : "translate-y-0"}`}>
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2 min-w-0">
        {/* Logo */}
        <Link to="/" className="font-heading font-black text-sm sm:text-lg md:text-xl tracking-tighter flex items-center gap-1.5 shrink-0" data-testid="public-logo">
          <Zap size={16} className="text-[var(--accent)] shrink-0" />
          <span className="hidden sm:inline">Campus Energy</span>
          <span className="sm:hidden">CEM</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={`px-2.5 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${loc.pathname === n.to ? "text-[var(--accent)] bg-[var(--accent-soft)] font-medium" : "text-[var(--t2)] hover:text-[var(--t1)]"}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={toggle} data-testid="public-theme-toggle" className="w-8 h-8 sm:w-9 sm:h-9 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors">
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          {user ? (
            <Link to="/app" data-testid="nav-dashboard" className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-md bg-[var(--accent)] text-black text-xs sm:text-sm font-semibold active:scale-95 transition-transform">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex px-4 py-2 rounded-md border border-[var(--bd)] text-sm font-medium hover:border-[var(--bd-strong)] transition-colors">Sign In</Link>
              <Link to="/register" data-testid="nav-register" className="hidden sm:inline-flex px-4 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-semibold active:scale-95 transition-transform">Get Started</Link>
            </>
          )}
          <button onClick={() => setOpen((o) => !o)} className="lg:hidden w-8 h-8 sm:w-9 sm:h-9 rounded-md border border-[var(--bd)] flex items-center justify-center" data-testid="public-menu-toggle">
            {open ? <X size={15} /> : <Menu size={15} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[var(--bd)] bg-[var(--bg)] px-4 py-3 flex flex-col gap-0.5 max-h-[calc(100dvh-56px)] overflow-y-auto">
          {/* Auth buttons — shown only on small screens where they're hidden from nav bar */}
          {!user && (
            <div className="sm:hidden flex gap-2 mb-3 pb-3 border-b border-[var(--bd)]">
              <Link to="/register" onClick={() => setOpen(false)}
                className="flex-1 py-2.5 rounded-md bg-[var(--accent)] text-black text-sm font-semibold text-center active:scale-95 transition-transform">
                Get Started
              </Link>
              <Link to="/login" onClick={() => setOpen(false)}
                className="flex-1 py-2.5 rounded-md border border-[var(--bd)] text-sm font-semibold text-center hover:border-[var(--bd-strong)] transition-colors">
                Sign In
              </Link>
            </div>
          )}
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              className={`px-3 py-2.5 rounded-md text-sm transition-colors ${loc.pathname === n.to ? "bg-[var(--surface2)] text-[var(--t1)] font-medium" : "text-[var(--t2)] hover:bg-[var(--surface2)] hover:text-[var(--t1)]"}`}>
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-[var(--bd)] bg-[var(--surface)]/40">
      <div className="max-w-[1300px] mx-auto px-6 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
        <div>
          <div className="font-heading font-black text-lg flex items-center gap-2"><Zap size={18} className="text-[var(--accent)]" /> Campus Energy Monitor</div>
          <p className="text-sm text-[var(--t2)] mt-3 leading-relaxed">Production-grade IoT platform for 3-phase campus energy metering, control and AI-assisted analytics. CPUT Final Year Project 2026.</p>
        </div>
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--t2)] mb-3">Pages</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <Link to="/" className="text-[var(--t2)] hover:text-[var(--t1)]">Home</Link>
            <Link to="/contact" className="text-[var(--t2)] hover:text-[var(--t1)]">Contact</Link>
            <Link to="/features" className="text-[var(--t2)] hover:text-[var(--t1)]">Features</Link>
            <Link to="/legal" className="text-[var(--t2)] hover:text-[var(--t1)]">Legal</Link>
            <Link to="/about" className="text-[var(--t2)] hover:text-[var(--t1)]">About</Link>
            <Link to="/code" className="text-[var(--t2)] hover:text-[var(--t1)]">Code</Link>
          </div>
        </div>
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--t2)] mb-3">Connect</div>
          <div className="flex gap-2 flex-wrap">
            {[
              [Github,       "https://github.com/henockhnk092-dot"],
              [Youtube,      "https://youtube.com/@HNK2005"],
              [Linkedin,     "https://linkedin.com"],
              [MessageCircle,"https://wa.me/27754531797"],
            ].map(([Icon, href], i) => (
              <a key={i} href={href} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-md border border-[var(--bd)] flex items-center justify-center text-[var(--t2)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors">
                <Icon size={16} />
              </a>
            ))}
            <a href="https://www.tiktok.com/@codingfever" target="_blank" rel="noreferrer"
              className="w-9 h-9 rounded-md border border-[var(--bd)] flex items-center justify-center text-[var(--t2)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.35a8.16 8.16 0 0 0 4.77 1.52V7.41a4.85 4.85 0 0 1-1-.72z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-[var(--bd)] py-4 text-center text-[10px] sm:text-xs text-[var(--t2)] font-mono px-4 break-words">© 2026 Henock Mukonkole, CPUT, Cape Town, South Africa</div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-[100dvh] bg-[var(--bg)] text-[var(--t1)] flex flex-col">
      <PublicNav />
      <div className="flex-1">{children}</div>
      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
